/* UTILS */
import { nextTick } from "vue"
import { setup, createActor } from "xstate"
import router from "@/router"
import { globalActor } from "@/machines/globalMachine"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"

/* STORES */
import { storeToRefs } from "pinia"
import { useUserStore } from "@/stores/user"

/* TYPES */
import { type GlobalMachineEvent } from "@/machines/types"

export const meetingMachine = setup({
  types: {} as {
    events: any
  },
  actions: {},
}).createMachine({
  id: "meetingMachine",
  initial: "book",
  states: {
    book: {
      entry: [() => router.replace({ name: "meeting" })],
      on: {
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export let meetingActor = createActor(meetingMachine)

const meetingActorObserver = {
  async complete() {
    const { fetchNextAssessment } = useUserStore()
    const { nextAssessmentName } = storeToRefs(useUserStore())

    const workflow_uuid = useCookies().get(COOKIES.WORKFLOW_UUID)
    await fetchNextAssessment(workflow_uuid ?? null)

    await nextTick()

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.WORKFLOW_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.PM_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.COMPANY_UUID, COOKIES.OPTIONS)
      globalActor.send({ type: "GLOBAL.MEETING.DONE" })
    }
  },
}

meetingActor.subscribe(meetingActorObserver)

/**
 * Observateurs extérieurs, réabonnés à chaque acteur neuf.
 *
 * Sans ça, un abonné qui garde la référence de l'acteur — le store d'aide, qui
 * en tire le nom de l'assessment courant, donc les CTA du header — resterait
 * branché sur l'acteur remplacé et deviendrait sourd.
 */
const meetingActorObservers: Array<(_snapshot: unknown) => void> = []

export function subscribeToMeetingActor(
  observer: (_snapshot: unknown) => void
) {
  meetingActorObservers.push(observer)
  meetingActor.subscribe(observer)
}

let hasMeetingActorStarted = false

/**
 * XState ignore `start()` sur un acteur déjà démarré (ou arrivé à son état
 * final) : aucune action d'entrée ne rejoue, la machine ne réapplique donc
 * jamais sa route et l'utilisateur reste sur la route parente nue, écran
 * blanc. Le cas se produit dès qu'on entre deux fois dans l'assessment au
 * cours de la même page — sortie par « non passé », ou renvoi dessus par le
 * workflow. On repart donc d'un acteur neuf.
 *
 * Mais UNIQUEMENT en entrant vraiment dans l'assessment, c'est-à-dire sur la
 * route parente nue, celle par laquelle la machine globale y fait entrer. Le
 * composant racine est aussi remonté sans qu'on change d'assessment — App.vue
 * reconstruit tout `main` quand la langue change — et repartir de zéro là
 * relancerait le test depuis son écran de chargement.
 */
export function startMeetingActor() {
  const isEnteringAssessment = router.currentRoute.value.name === "meeting"

  if (hasMeetingActorStarted && isEnteringAssessment) {
    meetingActor.stop()
    meetingActor = createActor(meetingMachine)
    meetingActor.subscribe(meetingActorObserver)
    for (const observer of meetingActorObservers)
      meetingActor.subscribe(observer)
  }

  hasMeetingActorStarted = true
  meetingActor.start()
}
